import { Box, Flex, Icon, Input, InputElement } from '@chakra-ui/react';
import { formatInteger, pluralize } from '@monetize/utils/core';
import React, {
  FunctionComponent as FC,
  useEffect,
  useRef,
  useState,
} from 'react';
import { MdOutlineSearch, MdSearch } from 'react-icons/md';
import { useDebounce } from '../../../hooks/useDebounce';
import useOutsideClick from '../../../hooks/useOutsideClick';
import { Tooltip } from '../../ui/tooltip';
import MCloseButton from '../MCloseButton';
import MCustomIconButton from '../MCustomIconButton';
import MText from '../MText';

const COUNT_TEXT_PX_PER_CHAR = 6.5;
const CLOSE_BUTTON_W = 32;
const SEARCH_ICON_ELEMENT_W = 32;

interface MPageSearchInputProps {
  value: string;
  count?: number;
  /**
   * If true, will show the count of results next to the search input. If false, will not show the count.
   * This was removed from default behavior since data tables show the count and loading state better than
   * this component can. And the value tends to be a bit jumpy (changes from no results, to 0, to 150, etc..)
   */
  showCount?: boolean;
  searchTooltip?: string;
  placeholderKey?: string;
  resetSearch?: any;
  mode?: 'collapsible' | 'alwaysExpanded';
  onChange: (val: string) => void;
}

const MPageSearchInput: FC<MPageSearchInputProps> = React.forwardRef<
  any,
  MPageSearchInputProps
>(
  (
    {
      value,
      count = 0,
      showCount = false,
      searchTooltip = 'Search',
      placeholderKey = '',
      resetSearch,
      mode = 'alwaysExpanded',
      onChange,
    }: MPageSearchInputProps,
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [internalValue, setInternalValue] = useState(value);
    const debouncedValue = useDebounce(internalValue);
    const [isOpenIfCollapsible, setIsOpen] = useState(false);
    const currentRef = React.useRef(null);

    const isOpen = mode === 'alwaysExpanded' ? true : isOpenIfCollapsible;

    useEffect(() => {
      setInternalValue(value);
    }, [value]);

    useEffect(() => {
      onChange(debouncedValue);
    }, [debouncedValue]);

    useEffect(() => {
      if (resetSearch) {
        const resetRef = resetSearch;
        resetRef.current = onSetEmpty;
      }
    }, []);

    useEffect(() => {
      if (isOpen && mode !== 'alwaysExpanded' && inputRef.current) {
        inputRef.current.focus();
      }
    }, [isOpen]);

    useEffect(() => {
      if (internalValue) {
        setIsOpen(true);
      }
    }, [internalValue]);

    const handleOnKeyDown = (ev: React.KeyboardEvent) => {
      if (ev.key === 'Enter') {
        onChange(internalValue);
      }
    };

    useOutsideClick({
      ref: currentRef,
      handler: () => internalValue === '' && setIsOpen(false),
    });

    const onSetEmpty = () => {
      onChange('');
      setIsOpen(false);
    };

    const countText = count
      ? `${formatInteger(count)} ${pluralize('result', count)}`
      : 'No Results';

    const mainInputP = 4;
    let inputRightElementW = 32;

    if (showCount) {
      const countTextWidth = Math.ceil(
        countText.length * COUNT_TEXT_PX_PER_CHAR,
      );
      inputRightElementW = countTextWidth + CLOSE_BUTTON_W;
    }

    return (
      <Tooltip
        disabled={isOpen}
        content={searchTooltip}
        positioning={{
          placement: 'bottom-end',
        }}
      >
        <Flex
          ref={currentRef}
          bg={isOpen ? 'tGray.support' : 'none'}
          borderRadius="3"
          maxWidth="400"
          alignItems="center"
          h="8"
          color="tPurple.base"
        >
          {!isOpen && (
            <MCustomIconButton
              variant="icon"
              data-testid="search-icon-button"
              containerSize="8"
              btnSize={6}
              fontSize="lg"
              cursor="pointer"
              icon={MdOutlineSearch}
              aria-label="Search"
              onClick={() => setIsOpen(true)}
            />
          )}
          {isOpen && (
            <Flex
              alignItems="center"
              width="277px"
              transition="width 1s ease-out"
            >
              <Box position="relative" width="100%">
                <InputElement
                  placement="start"
                  pointerEvents="none"
                  w="8"
                  textAlign="center"
                  top="0"
                  left="1"
                >
                  <Icon w="5" h="5" color="tPurple.base" asChild>
                    <MdSearch />
                  </Icon>
                </InputElement>
                <Input
                  data-testid="search-input"
                  type="text"
                  ref={inputRef}
                  placeholder={
                    placeholderKey ? `Search by ${placeholderKey}` : 'Search'
                  }
                  value={internalValue}
                  onChange={(e) => setInternalValue(e.target.value)}
                  onBlur={() => onChange(internalValue)}
                  onKeyDown={handleOnKeyDown}
                  _placeholder={{
                    color: 'tGray.darkGrayPurple',
                  }}
                  pl={`${SEARCH_ICON_ELEMENT_W + mainInputP}px`}
                  pr={`${inputRightElementW + mainInputP}px`}
                />

                {(value || internalValue) && (
                  <InputElement
                    placement="end"
                    w={inputRightElementW}
                    alignItems="center"
                    justifyContent="flex-end"
                    top="0"
                    right="-10px"
                  >
                    {value && showCount && (
                      <MText fontSize="xs" mr="1" whiteSpace="nowrap">
                        {countText}
                      </MText>
                    )}

                    {internalValue && (
                      <MCloseButton onClick={() => onSetEmpty()} mr={2} />
                    )}
                  </InputElement>
                )}
              </Box>
            </Flex>
          )}
        </Flex>
      </Tooltip>
    );
  },
);

export default MPageSearchInput;
